import { useState, useRef, useCallback, useEffect } from "react";
import { Camera, Upload, X, FileText, Trash2, Download, RefreshCw, ZoomIn, ChevronLeft, ChevronUp, ChevronDown } from "lucide-react";
import jsPDF from "jspdf";

export default function ScanToPdf() {
    const [pages, setPages] = useState([]); // { id, dataUrl, name }
    const [cameraOpen, setCameraOpen] = useState(false);
    const [stream, setStream] = useState(null);
    const [facingMode, setFacingMode] = useState("environment");
    const [preview, setPreview] = useState(null); // dataUrl for lightbox
    const [pdfName, setPdfName] = useState("iki-scan-file");
    const [generating, setGenerating] = useState(false);
    const [toast, setToast] = useState(null);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    // ── Camera ──────────────────────────────────────────────────────────────
    const startCamera = useCallback(async (facing = facingMode) => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            const legacyGetUserMedia =
                navigator.getUserMedia ||
                navigator.webkitGetUserMedia ||
                navigator.mozGetUserMedia ||
                navigator.msGetUserMedia;

            if (!legacyGetUserMedia) {
                showToast("Camera not supported in this browser. Try Chrome or Safari 14+.", "error");
                return;
            }

            const getLegacyStream = () =>
                new Promise((resolve, reject) =>
                    legacyGetUserMedia.call(navigator, { video: true }, resolve, reject)
                );
            try {
                const s = await getLegacyStream();
                if (stream) stream.getTracks().forEach((t) => t.stop());
                setStream(s);
                setCameraOpen(true);
                setFacingMode(facing);
            } catch {
                showToast("Camera access denied.", "error");
            }
            return;
        }

        if (stream) {
            stream.getTracks().forEach((t) => t.stop());
            setStream(null);
        }

        const idealConstraints = {
            video: {
                facingMode: { ideal: facing },
                width: { ideal: 1920 },
                height: { ideal: 1080 },
            },
        };

        const fallbackConstraints = { video: true };
        const tryGetUserMedia = async (constraints) =>
            navigator.mediaDevices.getUserMedia(constraints);

        try {
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const status = await navigator.permissions.query({ name: "camera" });
                    if (status.state === "denied") {
                        showToast("Camera is blocked. Please allow access in your browser settings.", "error");
                        return;
                    }
                } catch {
                    // Safari catch fallback
                }
            }

            let s;
            try {
                s = await tryGetUserMedia(idealConstraints);
            } catch (idealErr) {
                if (
                    idealErr.name === "OverconstrainedError" ||
                    idealErr.name === "ConstraintNotSatisfiedError" ||
                    idealErr.name === "NotFoundError"
                ) {
                    s = await tryGetUserMedia(fallbackConstraints);
                } else {
                    throw idealErr;
                }
            }

            setStream(s);
            setCameraOpen(true);
            setFacingMode(facing);
        } catch (err) {
            const messages = {
                NotAllowedError: "Camera access denied. Tap 'Allow' when your browser asks, or check Settings → Safari → Camera.",
                PermissionDeniedError: "Camera permission denied. Check your browser settings.",
                NotFoundError: "No camera found on this device.",
                NotReadableError: "Camera is in use by another app. Close it and try again.",
                AbortError: "Camera request was cancelled. Please try again.",
                SecurityError: "Camera requires a secure connection (HTTPS).",
                TypeError: "Camera not supported in this browser.",
            };
            const msg = messages[err.name] || `Camera error: ${err.message || err.name}`;
            showToast(msg, "error");
        }
    }, [stream, facingMode]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !stream) return;

        if ("srcObject" in video) {
            video.srcObject = stream;
        } else {
            try {
                video.src = URL.createObjectURL(stream);
            } catch {
                showToast("Could not attach camera stream.", "error");
                return;
            }
        }

        video.play().catch(() => { });
    }, [stream, cameraOpen]);

    const stopCamera = useCallback(() => {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        setStream(null);
        setCameraOpen(false);
    }, [stream]);

    const flipCamera = useCallback(() => {
        const next = facingMode === "environment" ? "user" : "environment";
        startCamera(next);
    }, [facingMode, startCamera]);

    const capture = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        const id = Date.now();
        setPages((prev) => [...prev, { id, dataUrl, name: `Page ${prev.length + 1}` }]);
        showToast(`Page ${pages.length + 1} captured!`, "success");
    }, [pages.length]);

    // ── File upload ──────────────────────────────────────────────────────────
    const handleFiles = useCallback((files) => {
        const imgs = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (!imgs.length) { showToast("Only image files are supported.", "error"); return; }
        imgs.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const id = Date.now() + Math.random();
                setPages((prev) => [...prev, { id, dataUrl: e.target.result, name: file.name.replace(/\.[^.]+$/, "") }]);
            };
            reader.readAsDataURL(file);
        });
        showToast(`${imgs.length} image${imgs.length > 1 ? "s" : ""} added.`, "success");
    }, []);

    // ── Page management ──────────────────────────────────────────────────────
    const removePage = (id) => setPages((prev) => prev.filter((p) => p.id !== id));
    const movePage = (id, dir) => {
        setPages((prev) => {
            const idx = prev.findIndex((p) => p.id === id);
            const next = idx + dir;
            if (next < 0 || next >= prev.length) return prev;
            const arr = [...prev];
            [arr[idx], arr[next]] = [arr[next], arr[idx]];
            return arr;
        });
    };

    // ── PDF generation ────────────────────────────────────────────────────────
    const generatePdf = async () => {
        if (!pages.length) { showToast("Add at least one page first.", "error"); return; }
        setGenerating(true);
        try {
            await new Promise((r) => setTimeout(r, 50));
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const W = 210, H = 297;

            for (let i = 0; i < pages.length; i++) {
                if (i > 0) pdf.addPage();
                const img = await loadImage(pages[i].dataUrl);
                const imgW = img.naturalWidth, imgH = img.naturalHeight;
                const ratio = Math.min(W / imgW, H / imgH);
                const w = imgW * ratio, h = imgH * ratio;
                const x = (W - w) / 2, y = (H - h) / 2;
                pdf.addImage(pages[i].dataUrl, "JPEG", x, y, w, h);
            }

            pdf.save(`${pdfName || "scan"}.pdf`);
            showToast("PDF downloaded!", "success");
        } catch (err) {
            showToast("Failed to generate PDF.", "error");
            console.error(err);
        }
        setGenerating(false);
    };

    const loadImage = (src) => new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = src;
    });

    // ── Toast ─────────────────────────────────────────────────────────────────
    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    return (
        <div className="min-h-screen bg-[#0F0F0F] text-white font-sans selection:bg-[#2F8F6F]/30">
            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} className="hidden" />

            {/* ── Toast ── */}
            {toast && (
                <div
                    className={`fixed top-6 left-1/2 -translate-x-1/2 z-[9999] rounded-full px-6 py-2.5 text-xs font-semibold shadow-2xl animate-fadeInDown border text-white
                        ${toast.type === "success" ? "bg-[#145A43] border-[#2F8F6F]" : "bg-red-950 border-red-500"}`}
                >
                    {toast.msg}
                </div>
            )}

            {/* ── Lightbox ── */}
            {preview && (
                <div
                    onClick={() => setPreview(null)}
                    className="fixed inset-0 z-[9998] bg-black/92 flex items-center justify-center backdrop-blur-sm"
                >
                    <img
                        src={preview}
                        alt="preview"
                        className="max-w-[90vw] max-h-[80vh] rounded-xl shadow-[0_0_60px_rgba(47,143,111,0.25)] border border-[#2F8F6F]/20 object-contain"
                    />
                    <button
                        onClick={() => setPreview(null)}
                        className="absolute top-5 right-5 bg-[#145A43] hover:bg-[#2F8F6F] border border-[#2F8F6F]/40 rounded-full w-10 height-10 text-white cursor-pointer flex items-center justify-center transition-colors shadow-lg"
                    >
                        <X size={18} />
                    </button>
                </div>
            )}

            {/* ── Camera overlay ── */}
            {cameraOpen && (
                <div className="fixed inset-0 z-[9990] bg-black flex flex-col items-center justify-center">
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-screen object-cover"
                    />

                    {/* document frame guide */}
                    {/* <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[85vh] max-w-[480px] max-h-[600px] border-2 border-[#2F8F6F] rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] pointer-events-none" /> */}

                    <div className="absolute bottom-10 flex gap-6 items-center z-10">
                        <button
                            onClick={stopCamera}
                            className="w-12 h-12 rounded-full bg-[#145A43] hover:bg-red-900 border border-[#2F8F6F]/50 text-white cursor-pointer flex items-center justify-center transition-colors shadow-md"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={capture}
                            className="w-20 h-20 rounded-full bg-[#2F8F6F] hover:bg-[#145A43] border-4 border-[#D7C7A1] cursor-pointer shadow-[0_0_30px_rgba(47,143,111,0.6)] flex items-center justify-center transition-all active:scale-95"
                        >
                            <Camera size={32} className="text-white" />
                        </button>
                        <button
                            onClick={flipCamera}
                            className="w-12 h-12 rounded-full bg-[#145A43] hover:bg-[#2F8F6F] border border-[#2F8F6F]/50 text-white cursor-pointer flex items-center justify-center transition-colors shadow-md"
                        >
                            <RefreshCw size={18} />
                        </button>
                    </div>

                    <div className="absolute top-6 color-[#D7C7A1] text-xs font-semibold bg-black/60 px-4 py-1.5 rounded-full border border-[#2F8F6F]/20">
                        {pages.length} page{pages.length !== 1 ? "s" : ""} captured
                    </div>
                </div>
            )}

            {/* ── Header ── */}
            <div className="bg-gradient-to-br from-[#0B3D2E] via-[#145A43] to-[#0B3D2E] border-b border-[#2F8F6F]/20 px-6 py-12 md:py-16 shadow-lg">
                <div className="max-w-2xl mx-auto flex flex-col justify-center items-center text-center space-y-3">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-[#D7C7A1]">
                        Scan to PDF
                    </h1>
                    <p className="text-[#D7C7A1] text-xs sm:text-sm max-w-md font-medium leading-relaxed">
                        Capture pages with your camera or upload images compiled into a clean PDF, right in your browser.
                    </p>
                </div>
            </div>

            {/* ── Main Container ── */}
            <div className="max-w-2xl mx-auto px-6 py-10">

                {/* ── Action row ── */}
                <div className="flex flex-wrap gap-4 justify-center mb-8">
                    <button
                        onClick={() => startCamera()}
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#2F8F6F] to-[#145A43] border border-[#2F8F6F] text-white font-bold text-sm cursor-pointer shadow-[0_4px_20px_rgba(47,143,111,0.35)] hover:brightness-110 active:scale-98 transition-all"
                    >
                        <Camera size={16} /> Capture Document
                    </button>

                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 hover:bg-white/10 border border-[#2F8F6F]/20 text-[#D7C7A1] font-bold text-sm cursor-pointer backdrop-blur-md active:scale-98 transition-all"
                    >
                        <Upload size={16} /> Upload Images
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                    />
                </div>

                {/* ── Pages list ── */}
                {pages.length > 0 && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-[#D7C7A1] tracking-wider uppercase">
                                Queue: {pages.length} page{pages.length !== 1 ? "s" : ""}
                            </span>
                            <button
                                onClick={() => setPages([])}
                                className="bg-transparent border-none text-red-400/70 hover:text-red-400 cursor-pointer text-xs font-semibold flex items-center gap-1 transition-colors"
                            >
                                <Trash2 size={14} /> Clear all
                            </button>
                        </div>

                        {/* Page cards stack */}
                        <div className="flex flex-col gap-2.5">
                            {pages.map((page, i) => (
                                <div
                                    key={page.id}
                                    className="flex items-center gap-4 bg-[#145A43]/10 border border-[#2F8F6F]/15 rounded-xl p-3 hover:border-[#2F8F6F]/30 transition-colors"
                                >
                                    {/* Thumbnail preview */}
                                    <div
                                        onClick={() => setPreview(page.dataUrl)}
                                        className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border border-[#2F8F6F]/30 cursor-pointer relative group"
                                    >
                                        <img src={page.dataUrl} alt={page.name} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
                                            <ZoomIn size={14} className="text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                                        </div>
                                    </div>

                                    {/* Metadata text */}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-white truncate">
                                            {page.name}
                                        </div>
                                        <div className="text-[11px] text-[#D7C7A1]/50 mt-0.5">Page {i + 1}</div>
                                    </div>

                                    {/* Sorting & action items */}
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => movePage(page.id, -1)}
                                            disabled={i === 0}
                                            className={`w-7 h-7 rounded-md flex items-center justify-center transition-all bg-[#2F8F6F]/10 text-[#2F8F6F]
                                                ${i === 0 ? "opacity-30 cursor-not-allowed text-[#2F8F6F]/40" : "hover:bg-[#2F8F6F]/20 cursor-pointer"}`}
                                        >
                                            <ChevronUp size={13} />
                                        </button>
                                        <button
                                            onClick={() => movePage(page.id, 1)}
                                            disabled={i === pages.length - 1}
                                            className={`w-7 h-7 rounded-md flex items-center justify-center transition-all bg-[#2F8F6F]/10 text-[#2F8F6F]
                                                ${i === pages.length - 1 ? "opacity-30 cursor-not-allowed text-[#2F8F6F]/40" : "hover:bg-[#2F8F6F]/20 cursor-pointer"}`}
                                        >
                                            <ChevronDown size={13} />
                                        </button>
                                        <button
                                            onClick={() => removePage(page.id)}
                                            className="w-7 h-7 rounded-md flex items-center justify-center bg-red-950/40 text-red-400 hover:bg-red-900/40 cursor-pointer transition-all"
                                        >
                                            <X size={13} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ── Export Actions Block ── */}
                        <div className="bg-[#145A43]/10 border border-[#2F8F6F]/20 rounded-xl p-5 space-y-3">
                            <label className="text-xs font-bold text-[#D7C7A1] block tracking-wide">
                                PDF Filename
                            </label>
                            <div className="flex flex-col w-full gap-3 items-center">
                                <div className="flex items-center flex-1 w-full bg-[#0B3D2E] border border-[#2F8F6F]/30 rounded-lg px-3 focus-within:border-[#2F8F6F] transition-colors">
                                    <input
                                        value={pdfName}
                                        onChange={(e) => setPdfName(e.target.value)}
                                        placeholder="scan"
                                        className="bg-transparent border-none outline-none text-white text-sm py-2.5 flex-1"
                                    />
                                    <span className="text-[#2F8F6F]/60 text-xs font-medium">.pdf</span>
                                </div>
                                <button
                                    onClick={generatePdf}
                                    disabled={generating}
                                    className={`flex items-center w-full justify-center gap-2 px-6 py-2.5 rounded-lg border text-white font-bold text-sm whitespace-nowrap transition-all select-none
                                        ${generating
                                            ? "bg-[#2F8F6F]/30 border-[#2F8F6F]/20 cursor-not-allowed text-white/50"
                                            : "bg-gradient-to-br from-[#2F8F6F] to-[#145A43] border-[#2F8F6F] hover:brightness-110 active:scale-98 cursor-pointer"}`}
                                >
                                    {generating ? (
                                        <RefreshCw size={15} className="animate-spin" />
                                    ) : (
                                        <Download size={15} />
                                    )}
                                    {generating ? "Generating…" : "Download PDF"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Empty state ── */}
                {pages.length === 0 && (
                    <div className="text-center py-12 px-4 color-[#D7C7A1] flex flex-col justify-center items-center border border-dashed border-[#2F8F6F]/30 rounded-xl bg-white/[0.01]">
                        <FileText size={44} className="mb-3 opacity-20 text-[#2F8F6F]" />
                        <p className="margin-0 text-xs sm:text-sm font-medium text-[#D7C7A1]/70">
                            No pages yet — capture or upload images to get started.
                        </p>
                    </div>
                )}

                {/* ── Footer note ── */}
                <p className="text-[11px] text-[#2F8F6F]/40 text-center mt-12 font-medium tracking-wide">
                    All processing happens locally in your browser. Nothing is ever uploaded to a server.
                </p>
            </div>

            {/* Custom keyframes injection for elements needing specific non-standard triggers */}
            <style>{`
                @keyframes fadeInDown {
                    from { opacity: 0; transform: translate(-50%, -8px); }
                    to   { opacity: 1; transform: translate(-50%, 0); }
                }
                .animate-fadeInDown {
                    animation: fadeInDown 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>

            <div className="text-gray-400 flex w-full justify-center text-xs md:text-md text-end bg-[#0F0F0F] pb-10">
                <p>Copyright © 2025 Rizki Makruf. All rights reserved.</p>
            </div>
        </div>
    );
}